import { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Select,
  MenuItem,
  TextField,
  FormControl,
  InputLabel,
  Paper,
  Chip
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import type { RestricaoProduto } from '../hooks/usePoliticasVendedor';

interface Props {
  restricoes: RestricaoProduto[];
  grupos: string[];
  subgrupos: string[];
  onAdd: (payload: {
    tipo: 'grupo' | 'subgrupo' | 'produto';
    codigo?: number;
    grupo?: string;
    subgrupo?: string;
  }) => void;
  onRemove: (id: string) => void;
  isLoading?: boolean;
}

export function ProdutosBlacklist({
  restricoes,
  grupos,
  subgrupos,
  onAdd,
  onRemove,
  isLoading
}: Props) {
  const [tipo, setTipo] = useState<'grupo' | 'subgrupo' | 'produto'>('grupo');
  const [valor, setValor] = useState('');
  const [adding, setAdding] = useState(false);

  const handleAdd = () => {
    if (!valor) return;
    const payload: any = { tipo };
    if (tipo === 'grupo') payload.grupo = valor;
    else if (tipo === 'subgrupo') payload.subgrupo = valor;
    else payload.codigo = Number(valor);
    onAdd(payload);
    setValor('');
    setAdding(false);
  };

  const getLabel = (r: RestricaoProduto): string => {
    if (r.tipo === 'grupo') return `Grupo: ${r.grupo}`;
    if (r.tipo === 'subgrupo') return `Subgrupo: ${r.subgrupo}`;
    return `Produto: ${r.codigo}`;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle2">Produtos Bloqueados</Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={() => setAdding(true)}>
          Adicionar Restricao
        </Button>
      </Box>

      {adding && (
        <Paper
          variant="outlined"
          sx={{ p: 1.5, mb: 1, display: 'flex', gap: 1, alignItems: 'center' }}
        >
          <FormControl size="small" sx={{ width: 130 }}>
            <InputLabel>Tipo</InputLabel>
            <Select
              value={tipo}
              label="Tipo"
              onChange={(e) => {
                setTipo(e.target.value as any);
                setValor('');
              }}
            >
              <MenuItem value="grupo">Grupo</MenuItem>
              <MenuItem value="subgrupo">Subgrupo</MenuItem>
              <MenuItem value="produto">Codigo</MenuItem>
            </Select>
          </FormControl>
          {tipo === 'grupo' ? (
            <FormControl size="small" sx={{ flex: 1 }}>
              <InputLabel>Grupo</InputLabel>
              <Select value={valor} label="Grupo" onChange={(e) => setValor(e.target.value)}>
                {grupos.map((g) => (
                  <MenuItem key={g} value={g}>
                    {g}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : tipo === 'subgrupo' ? (
            <FormControl size="small" sx={{ flex: 1 }}>
              <InputLabel>Subgrupo</InputLabel>
              <Select value={valor} label="Subgrupo" onChange={(e) => setValor(e.target.value)}>
                {subgrupos.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <TextField
              size="small"
              sx={{ flex: 1 }}
              label="Codigo do Produto"
              type="number"
              value={valor}
              onChange={(e) => setValor(e.target.value)}
            />
          )}
          <Button
            size="small"
            variant="contained"
            onClick={handleAdd}
            disabled={!valor || isLoading}
          >
            OK
          </Button>
          <Button
            size="small"
            onClick={() => {
              setAdding(false);
              setValor('');
            }}
          >
            Cancelar
          </Button>
        </Paper>
      )}

      {restricoes.length === 0 && !adding && (
        <Typography variant="body2" color="text.secondary">
          Nenhuma restricao -- todos os produtos disponiveis.
        </Typography>
      )}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {restricoes.map((r) => (
          <Chip
            key={r.id}
            label={getLabel(r)}
            onDelete={() => onRemove(r.id)}
            color="warning"
            variant="outlined"
            size="small"
          />
        ))}
      </Box>
    </Box>
  );
}
